import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import palette from 'theme/palette'

import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import { Box, Tooltip } from '@mui/material'

import LoadingIconButton from 'components/LoadingIconButton'

import { TRANSLATION_KEYS } from '../constants'
import { tableStyles } from '../styles'

interface TableHeaderProps {
  columns: string[]
  hasNextPage?: boolean
  isLoadingMore?: boolean
  onLoadMore?: () => void
}

export const TableHeader = memo(
  ({
    columns,
    hasNextPage = false,
    isLoadingMore = false,
    onLoadMore,
  }: TableHeaderProps) => {
    const { t } = useTranslation('pondOverview')

    return (
      <div style={tableStyles.headerRow}>
        <div
          style={{
            ...tableStyles.headerCell,
            ...tableStyles.stickyCol1,
            zIndex: 15,
            backgroundColor: palette.light.grey[200],
            borderLeft: 'none',
          }}
        >
          {t(TRANSLATION_KEYS.HEADER.POND)}
        </div>
        <div
          style={{
            ...tableStyles.headerCell,
            ...tableStyles.stickyCol2,
            zIndex: 15,
            backgroundColor: palette.light.grey[200],
          }}
        >
          {t(TRANSLATION_KEYS.HEADER.SECTION)}
        </div>
        <div
          style={{
            ...tableStyles.headerCell,
            ...tableStyles.stickyCol3,
            zIndex: 15,
            borderRight: '4px solid ' + palette.light.grey[300],
            backgroundColor: palette.light.grey[200],
            // The load-more button (web only) is absolutely positioned at
            // right:-18px so it straddles the pinned/date boundary. stickyCol3
            // inherits overflow:hidden (pinnedTextClip) which clipped that
            // overhang, so on web the arrow button looked cut off. Allow this
            // one header cell to overflow; the label is a short fixed word so
            // it won't bleed into the date columns.
            overflow: 'visible',
          }}
        >
          {t(TRANSLATION_KEYS.HEADER.BACTERIA)}
          {hasNextPage && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                right: '-18px',
                transform: 'translateY(-50%)',
                zIndex: 1000,
              }}
            >
              <Tooltip title={t(TRANSLATION_KEYS.MESSAGES.LOAD_MORE)}>
                <span>
                  <LoadingIconButton
                    loading={isLoadingMore}
                    onClick={onLoadMore}
                    disabled={isLoadingMore}
                    style={{
                      background: 'white',
                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <KeyboardBackspaceIcon />
                  </LoadingIconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </div>
        {columns.map((col, idx) => (
          <div key={idx} style={tableStyles.headerCell}>
            {col}
          </div>
        ))}
      </div>
    )
  }
)

TableHeader.displayName = 'TableHeader'
